/**
 * Markdown Renderer Component
 * ============================
 * Renders markdown content with support for:
 * - Tables
 * - Code blocks with syntax highlighting
 * - Images
 * - Lists (ordered/unordered)
 * - Bold, italic, links
 * - Headings
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Box } from '@mui/material';

interface MarkdownRendererProps {
  content: string;
  sx?: any;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, sx = {} }) => {
  return (
    <Box
      sx={{
        '& p': {
          margin: '0.5em 0',
          lineHeight: 1.6,
        },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          margin: '1em 0 0.5em 0',
          fontWeight: 600,
        },
        '& ul, & ol': {
          margin: '0.5em 0',
          paddingLeft: '1.5em',
        },
        '& li': {
          margin: '0.25em 0',
        },
        '& code': {
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          padding: '0.2em 0.4em',
          borderRadius: '3px',
          fontFamily: 'monospace',
          fontSize: '0.9em',
        },
        '& pre': {
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          padding: '1em',
          borderRadius: '4px',
          overflow: 'auto',
          margin: '0.5em 0',
        },
        '& pre code': {
          backgroundColor: 'transparent',
          padding: 0,
        },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          margin: '0.5em 0',
          fontSize: '0.9em',
        },
        '& th, & td': {
          border: '1px solid #ddd',
          padding: '8px 12px',
          textAlign: 'left',
        },
        '& th': {
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          fontWeight: 600,
        },
        '& tr:nth-of-type(even)': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
        '& img': {
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '4px',
          margin: '0.5em 0',
        },
        '& a': {
          color: '#1976d2',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
        '& blockquote': {
          borderLeft: '4px solid #ddd',
          paddingLeft: '1em',
          margin: '0.5em 0',
          color: '#666',
        },
        '& hr': {
          border: 'none',
          borderTop: '1px solid #ddd',
          margin: '1em 0',
        },
        ...sx
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer;
